/**
 * Dashboard V2 date-range helpers.
 *
 * Report tables bucket by civil day in {@link DASHBOARD_REPORTING_TIMEZONE}
 * (see write sync `DATE(ts, tz)`). These helpers convert picker `Date`s to/from
 * that same calendar so read queries match write-side day keys.
 */
export const DASHBOARD_REPORTING_TIMEZONE = 'America/New_York';

const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export type UtcDashboardDateRangeQuery = {
  datefrom: string;
  dateto: string;
};

/** Format a moment as `YYYY-MM-DD` in the dashboard reporting timezone. */
function formatReportingCalendarDate(date: Date): string {
  // en-CA yields YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DASHBOARD_REPORTING_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

function parseReportingCalendarDate(dateStr: string): Date | undefined {
  const match = CALENDAR_DATE_RE.exec(dateStr.trim());
  if (!match) {
    return undefined;
  }
  // Noon UTC keeps the civil date stable across America/New_York offsets when
  // round-tripping through formatReportingCalendarDate.
  return new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], 12, 0, 0, 0));
}

function addReportingCalendarDays(dateStr: string, days: number): string {
  const d = parseReportingCalendarDate(dateStr);
  if (!d) {
    return dateStr;
  }
  d.setUTCDate(d.getUTCDate() + days);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Picker dates → reporting-TZ `[start, end)` day boundaries (`YYYY-MM-DD`)
 * for dashboard V2 (must match write-side `DATE(ts, DASHBOARD_REPORTING_TIMEZONE)`).
 */
export function toUtcDashboardDateRangeQuery(
  from: Date,
  to: Date
): UtcDashboardDateRangeQuery {
  const datefrom = formatReportingCalendarDate(from);
  const dateto = addReportingCalendarDays(formatReportingCalendarDate(to), 1);
  return { datefrom, dateto };
}

/** Stored reporting-TZ range → inclusive picker `from` / `to` dates. */
export function fromUtcDashboardDateRangeQuery(
  query: UtcDashboardDateRangeQuery
): { from: Date; to: Date } {
  const from =
    parseReportingCalendarDate(query.datefrom) ?? new Date(query.datefrom);
  const endExclusive = parseReportingCalendarDate(query.dateto);
  const to = endExclusive
    ? new Date(
        Date.UTC(
          endExclusive.getUTCFullYear(),
          endExclusive.getUTCMonth(),
          endExclusive.getUTCDate() - 1,
          12,
          0,
          0,
          0
        )
      )
    : new Date(query.dateto);
  return { from, to };
}

export function getDefaultUtcDashboardDateRange(
  days = 30
): UtcDashboardDateRangeQuery {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return toUtcDashboardDateRangeQuery(start, end);
}
